import { isDevBrowserModeEnabled } from "./devBrowser";
const TELEGRAM_SDK_URL = 'https://telegram.org/js/telegram-web-app.js?62'
const SDK_SCRIPT_ID = 'telegram-web-app-sdk'
const INIT_DATA_SESSION_KEY = 'citychronicle.telegram.initData'

let sdkLoadingPromise: Promise<boolean> | null = null

export function initializeTelegramWebApp(): void {
  if (isDevBrowserModeEnabled()) return
  configureTelegramWebApp()
  void loadTelegramSdk().then(() => configureTelegramWebApp())
}

export async function waitForTelegramInitData(timeoutMs = 6_000): Promise<string> {
  if (isDevBrowserModeEnabled()) return ""
  const immediate = readTelegramInitData()
  if (immediate) return immediate

  void loadTelegramSdk()
  const startedAt = Date.now()

  while (Date.now() - startedAt < timeoutMs) {
    const initData = readTelegramInitData()
    if (initData) return initData
    await delay(100)
  }

  return readTelegramInitData()
}

export function readTelegramInitData(): string {
  if (isDevBrowserModeEnabled()) return ""
  const sdkInitData = window.Telegram?.WebApp?.initData?.trim()
  if (sdkInitData) return rememberInitData(sdkInitData)

  const launchInitData = readLaunchParameter('tgWebAppData')?.trim()
  if (launchInitData) return rememberInitData(launchInitData)

  return readSessionInitData()
}

function loadTelegramSdk(timeoutMs = 5_000): Promise<boolean> {
  if (window.Telegram?.WebApp) return Promise.resolve(true)
  if (sdkLoadingPromise) return sdkLoadingPromise

  sdkLoadingPromise = new Promise<boolean>((resolve) => {
    const existing = document.getElementById(SDK_SCRIPT_ID) as HTMLScriptElement | null
    const script = existing ?? document.createElement('script')
    let settled = false

    const finish = (loaded: boolean) => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      resolve(loaded && Boolean(window.Telegram?.WebApp))
    }

    const timer = window.setTimeout(() => finish(Boolean(window.Telegram?.WebApp)), timeoutMs)
    script.addEventListener('load', () => finish(true), { once: true })
    script.addEventListener('error', () => finish(false), { once: true })

    if (!existing) {
      script.id = SDK_SCRIPT_ID
      script.src = TELEGRAM_SDK_URL
      script.async = true
      document.head.appendChild(script)
    }
  })

  return sdkLoadingPromise
}

function configureTelegramWebApp(): void {
  const telegram = window.Telegram?.WebApp
  if (!telegram) return

  try {
    telegram.ready()
    telegram.expand()
    telegram.setHeaderColor('#171a22')
    telegram.setBackgroundColor('#111319')
  } catch (error) {
    console.warn('Telegram WebApp initialization failed', error)
  }
}

function readLaunchParameter(name: string): string | null {
  const searchValue = new URLSearchParams(window.location.search).get(name)
  if (searchValue) return searchValue

  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash
  if (!hash) return null
  return new URLSearchParams(hash).get(name)
}

function rememberInitData(initData: string): string {
  try {
    window.sessionStorage.setItem(INIT_DATA_SESSION_KEY, initData)
  } catch {
    // Telegram WebView may restrict storage; the current value is still usable.
  }
  return initData
}

function readSessionInitData(): string {
  try {
    return window.sessionStorage.getItem(INIT_DATA_SESSION_KEY)?.trim() ?? ''
  } catch {
    return ''
  }
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds))
}
